import React, {useEffect, useState} from 'react';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import PropTypes from 'prop-types';
import TwoToneCheckbox from './TwoToneCheckbox.jsx';

/**
 * @param {CheckboxWithOptionsListOfPREditors.propTypes} props
 * @return {JSX.Element}
 * @constructor
 */
export function CheckboxWithOptionsListOfPREditors(props) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [currentPageCheckbox, setCurrentPageCheckbox] = useState(false);
    const [allPagesCheckbox, setAllPagesCheckbox] = useState(false);

    const selectableCurrentPageRequests = props.displayedRequests.map((request) => request.tag);

    const isPageEntirelySelected = selectableCurrentPageRequests.every((pr) => props.selectedPRs.includes(pr));
    const isPagePartiallySelected = selectableCurrentPageRequests.some((pr) => props.selectedPRs.includes(pr));

    useEffect(() => {
        if (props.selectedPRs.length === 0) {
            props.setSelectPRsMode(false);
            setCurrentPageCheckbox(false);
            setAllPagesCheckbox(false);
        } else {
            props.setSelectPRsMode(true);
        }
    }, [props.selectedPRs.length]);

    useEffect(() => {
        if (props.massSubmitLoading || props.massDeleteLoading) {
            setCurrentPageCheckbox(false);
            setAllPagesCheckbox(false);
        }
    }, [props.massSubmitLoading, props.massDeleteLoading]);

    const toggleMenu = (event) => {
        if (anchorEl) {
            setAnchorEl(null);
        } else {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleNestedCheckboxChange = () => {
        if (allPagesCheckbox) {
            setAllPagesCheckbox(false);
        }
        if (!currentPageCheckbox) {
            selectCurrentPageRequests();
            setCurrentPageCheckbox(true);
        } else {
            deselectCurrentPageRequests();
            setCurrentPageCheckbox(false);
        }
    };

    const selectCurrentPageRequests = () => {
        const newSelectedPRs = [...new Set([...props.selectedPRs, ...selectableCurrentPageRequests])];
        const updatedFailedMassSubmittedPRs = props.failedMassSubmittedPRs.filter((pr) => !newSelectedPRs.includes(pr.prTag));
        props.setFailedMassSubmittedPRs(updatedFailedMassSubmittedPRs);
        props.setSelectedPRs(newSelectedPRs);
    };

    const deselectCurrentPageRequests = () => {
        const newSelectedPRs = props.selectedPRs.filter((tag) => !selectableCurrentPageRequests.includes(tag));
        const updatedFailedMassSubmittedPRs = props.failedMassSubmittedPRs.filter((pr) => !selectableCurrentPageRequests.includes(pr.prTag));
        props.setFailedMassSubmittedPRs(updatedFailedMassSubmittedPRs);
        props.setSelectedPRs(newSelectedPRs);
    };

    const menuItems = [
        <MenuItem key="current">
            <FormGroup>
                <FormControlLabel
                    control={
                        <TwoToneCheckbox
                            checked={currentPageCheckbox}
                            onChange={() => handleNestedCheckboxChange()}
                            disabled={props.disabled}
                        />
                    }
                    label={
                        <span>
                            Select all <strong>filtered</strong> requests on <strong>displayed page</strong>
                        </span>
                    }
                />
            </FormGroup>
        </MenuItem>,
    ];

    return (
        <div>
            <IconButton
                onClick={toggleMenu}
                disabled={props.disabled}
                sx={{
                    '&:hover': {
                        backgroundColor: 'transparent',
                    },
                    '& .MuiTouchRipple-root': {
                        display: 'none',
                    },
                }}>
                <TwoToneCheckbox
                    checked={isPageEntirelySelected}
                    disabled={props.disabled}
                    indeterminate={!isPageEntirelySelected && isPagePartiallySelected}
                />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={toggleMenu}
            >
                {menuItems}
            </Menu>
        </div>
    );
}

CheckboxWithOptionsListOfPREditors.propTypes = {
    displayedRequests: PropTypes.array.isRequired,
    selectedPRs: PropTypes.array.isRequired,
    setSelectedPRs: PropTypes.func.isRequired,
    setSelectPRsMode: PropTypes.func.isRequired,
    failedMassSubmittedPRs: PropTypes.array,
    setFailedMassSubmittedPRs: PropTypes.func,
    massSubmitLoading: PropTypes.bool,
    massDeleteLoading: PropTypes.bool,
    disabled: PropTypes.bool,
    currentTab: PropTypes.string,
};
